import { Close, Save } from '@mui/icons-material';
import { Box, Button, ButtonGroup, Fade, FormControl, Grid, MenuItem, TextField } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';
import { FC, useState } from 'react';
import { Button as SMButton, Modal, SMBSelect } from '../../../components';
import { getPlanRoomCategories, postPlanRoom } from '../../../fetch';
import { IPlanRoom, IPlanRoomCategory } from '../../../models/plan-rooms';

interface IManagePlanRoomsModal {
    open: boolean;
    onClose: () => void;
    fetchPlanRooms: () => Promise<void>;
}

export const ManagePlanRoomsModal: FC<IManagePlanRoomsModal> = ({
    open,
    onClose,
    fetchPlanRooms
}) => {
    const handleManagePlanRoomsClose = () => {
        // reset state
        setPlanRoomName('');
        setSelectedPlanRoomCategory('');

        onClose();
    };
    const { enqueueSnackbar } = useSnackbar();
    const [selectedPlanRoomCategory, setSelectedPlanRoomCategory] = useState('');
    const [planRoomName, setPlanRoomName] = useState('');
    const [planRoomCategories, setPlanRoomCategories] = useState<IPlanRoomCategory[]>([]);
    const [isSaving, setSaving] = useState(false);
    const [isLoadingPlanRoomCategories, setIsLoadingPlanRoomCategories] = useState(false);

    const fetchPlanRoomCategories = async () => {
        try {
            setIsLoadingPlanRoomCategories(true);
            const categories = await getPlanRoomCategories();
            setPlanRoomCategories(categories);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoadingPlanRoomCategories(false);
        }
    }

    useEffect(() => {
        fetchPlanRoomCategories();
    }, []);

    const createPlanRoom = async (createablePlanRoom: IPlanRoom) => {
        try {
            setSaving(true);
            await postPlanRoom(createablePlanRoom.name, createablePlanRoom.category);
            await fetchPlanRooms();
            handleManagePlanRoomsClose();
            enqueueSnackbar('Plan Room has been created!', {
                variant: 'success'
            });
        } catch (error) {
            enqueueSnackbar(error?.Detail ?? `There was a problem creating the Plan Room`, {
                variant: 'error'
            });
        } finally {
            setSaving(false);
        }
    };


    return (
        <>
            <Modal
                open={open}
                onClose={handleManagePlanRoomsClose}
                maxWidth="sm"
                title={'Add Plan Room'}
            >
                <Fade in={open}>
                    <div>
                        <Box marginTop={1}>
                            <Grid container spacing={1}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <FormControl
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        required
                                    >
                                        <SMBSelect
                                            name="category"
                                            label="Category"
                                            disabled={isLoadingPlanRoomCategories}
                                            id="category"
                                            value={selectedPlanRoomCategory}
                                            variant="outlined"
                                            onChange={({ target: { value } }) => {
                                                setSelectedPlanRoomCategory(value);
                                            }}
                                        >
                                            {planRoomCategories &&
                                                planRoomCategories.map((planRoomCategory, index) => {
                                                    return (
                                                        <MenuItem key={`${index}`} value={planRoomCategory.value}>
                                                            {planRoomCategory.description}
                                                        </MenuItem>
                                                    );
                                                })}
                                        </SMBSelect>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <FormControl fullWidth variant="outlined">
                                        <TextField
                                            fullWidth
                                            required
                                            variant="outlined"
                                            size="small"
                                            name="planRoomName"
                                            autoComplete="none"
                                            label="Plan Room Name"
                                            value={planRoomName}
                                            onBlur={e => { }}
                                            onChange={e => {
                                                setPlanRoomName(e.target.value);
                                            }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box marginTop="2rem">
                                        <ButtonGroup>
                                            <SMButton
                                                id="save"
                                                color="primary"
                                                variant="contained"
                                                disabled={
                                                    !planRoomName ||
                                                    isLoadingPlanRoomCategories ||
                                                    !selectedPlanRoomCategory
                                                }
                                                loading={isSaving ? 'Saving...' : ''}
                                                startIcon={<Save />}
                                                onClick={async () => {
                                                    setSaving(true);
                                                    await createPlanRoom({
                                                        id: 0,
                                                        name: planRoomName,
                                                        category: selectedPlanRoomCategory
                                                    });
                                                }}
                                            >
                                                Save
                                            </SMButton>
                                            <Button
                                                id="cancel"
                                                variant="contained"
                                                color="inherit"
                                                startIcon={<Close />}
                                                onClick={() => {
                                                    handleManagePlanRoomsClose();
                                                }}
                                                classes="MuiButton-disableElevation"
                                            >
                                                Cancel
                                            </Button>
                                        </ButtonGroup>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>
                    </div>
                </Fade>
            </Modal>
        </>
    );
};
